import { SOUND_CATEGORIES, type SoundCategory } from "./SoundCategory.js";
import type { SoundDefinition } from "./SoundDefinition.js";

/** 마스터 게인 상한 — 그룹/로컬(0~1)과 달리 전체 음량 헤드룸을 위해 1 초과를 허용한다. */
export const MAX_MASTER_GAIN = 8;

/**
 * 글로벌·그룹·로컬·정의 볼륨을 모두 곱해 최종 값을 계산하는 순수 상태 보관소.
 *
 * 공식: `final = master × group[category] × local[id] × (def.volume ?? 1)`
 * mute: master mute 또는 그룹 mute가 true면 즉시 0.
 *
 * 그룹·로컬 setter는 0~1로 clamp(상대 볼륨). **master는 전체 음량 헤드룸용으로 0~MAX_MASTER_GAIN**
 * (1 초과 허용 — 작은 def.volume 가중치를 일괄 증폭). 호출자 입력 검증 부담 해소.
 * 본 클래스는 IO/타이머/콜백 없음 — 단위테스트 100% 커버 가능.
 */
export class VolumeMixer {
  private masterVolume = 1;
  private masterMuted = false;
  private readonly groupVolumes: Record<SoundCategory, number>;
  private readonly groupMuted: Record<SoundCategory, boolean>;
  private readonly localVolumes = new Map<string, number>();

  constructor(initial?: {
    master?: number;
    masterMuted?: boolean;
    groups?: Partial<Record<SoundCategory, number>>;
    groupsMuted?: Partial<Record<SoundCategory, boolean>>;
  }) {
    if (initial?.master !== undefined) this.masterVolume = clampMaster(initial.master);
    if (initial?.masterMuted !== undefined) this.masterMuted = initial.masterMuted;
    this.groupVolumes = Object.fromEntries(
      SOUND_CATEGORIES.map((cat) => [cat, clamp01(initial?.groups?.[cat] ?? 1)]),
    ) as Record<SoundCategory, number>;
    this.groupMuted = Object.fromEntries(
      SOUND_CATEGORIES.map((cat) => [cat, initial?.groupsMuted?.[cat] ?? false]),
    ) as Record<SoundCategory, boolean>;
  }

  setMasterVolume(value: number): void {
    this.masterVolume = clampMaster(value);
  }
  getMasterVolume(): number {
    return this.masterVolume;
  }

  setMasterMuted(muted: boolean): void {
    this.masterMuted = muted;
  }
  isMasterMuted(): boolean {
    return this.masterMuted;
  }

  setGroupVolume(category: SoundCategory, value: number): void {
    this.groupVolumes[category] = clamp01(value);
  }
  getGroupVolume(category: SoundCategory): number {
    return this.groupVolumes[category];
  }

  setGroupMuted(category: SoundCategory, muted: boolean): void {
    this.groupMuted[category] = muted;
  }
  isGroupMuted(category: SoundCategory): boolean {
    return this.groupMuted[category];
  }

  setLocalVolume(id: string, value: number): void {
    this.localVolumes.set(id, clamp01(value));
  }
  getLocalVolume(id: string): number {
    return this.localVolumes.get(id) ?? 1;
  }

  /**
   * 정의 1개의 최종 재생 볼륨 (master 헤드룸 때문에 1 초과 가능 — WebAudio gain은 1 초과 허용).
   * master/group 중 하나라도 mute이면 0.
   */
  computeEffectiveVolume(def: SoundDefinition): number {
    if (this.masterMuted || this.groupMuted[def.category]) return 0;
    const localVol = this.localVolumes.get(def.id) ?? 1;
    const defVol = def.volume ?? 1;
    return this.masterVolume * this.groupVolumes[def.category] * localVol * defVol;
  }
}

/** 그룹·로컬·def 볼륨 clamp (상대 볼륨 0~1). */
function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

/** master 게인 clamp (0~MAX_MASTER_GAIN — 전체 음량 헤드룸 허용). */
function clampMaster(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > MAX_MASTER_GAIN) return MAX_MASTER_GAIN;
  return value;
}
